import { InternalMenuItems, InternalMenuItem } from './Menu';
import { flatten } from 'lodash';

const getSubMenuFromPath = (menuItems: InternalMenuItems, focusIndexStack: number[]): InternalMenuItems => {
  if (focusIndexStack.length === 1) {
    return menuItems;
  }
  // submenu should always be defined if our logic elsewhere is right
  const focusedItem = flatten(menuItems)[focusIndexStack[0]];
  if (focusedItem.subMenu !== undefined) {
    return getSubMenuFromPath(focusedItem.subMenu, focusIndexStack.slice(1));
  }
  // should be unreachable
  return menuItems;
};

export const getMenuItemFromPath = (
  menuItems: InternalMenuItems,
  focusIndexStack: number[],
): InternalMenuItem | undefined =>
  flatten(getSubMenuFromPath(menuItems, focusIndexStack))[focusIndexStack[focusIndexStack.length - 1]];

const getNextEnabledMenuItemIndex = (flatMenuItems: InternalMenuItem[], currentIndex: number): number =>
  currentIndex + (flatMenuItems.slice(currentIndex + 1).findIndex((mi) => !mi.disabled) + 1);

const getPreviousEnabledMenuItemIndex = (flatMenuItems: InternalMenuItem[], currentIndex: number): number =>
  currentIndex -
  (flatMenuItems
    .slice(0, currentIndex)
    .reverse()
    .findIndex((mi) => !mi.disabled) +
    1);

const getFirstEnabledMenuItemIndex = (flatMenuItems: InternalMenuItem[]): number =>
  flatMenuItems.findIndex((mi) => !mi.disabled);

export type NavigationKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'Escape';
export const NAVIGATION_KEYS: NavigationKey[] = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
export type ActionKey = 'Space' | 'Enter' | 'Escape';
export const ACTION_KEYS: ActionKey[] = ['Space', 'Enter', 'Escape'];

export const getNewFocusIndexStack = (
  menuItems: InternalMenuItems,
  focusIndexStack: number[],
  key: NavigationKey,
): number[] => {
  if (focusIndexStack.length === 0) {
    if (key === 'ArrowDown') {
      const firstEnabledIndex = getFirstEnabledMenuItemIndex(flatten(menuItems));
      return firstEnabledIndex === -1 ? [] : [firstEnabledIndex];
    }
    return [];
  }

  const flatFocusedMenu = flatten(getSubMenuFromPath(menuItems, focusIndexStack));
  const focusedItemIndexInMenu = focusIndexStack[focusIndexStack.length - 1];
  const focusedItem = flatFocusedMenu[focusedItemIndexInMenu];
  const focusIndexMenuPath = focusIndexStack.slice(0, focusIndexStack.length - 1);

  if (key === 'ArrowUp') {
    return [...focusIndexMenuPath, getPreviousEnabledMenuItemIndex(flatFocusedMenu, focusedItemIndexInMenu)];
  }
  if (key === 'ArrowDown') {
    return [...focusIndexMenuPath, getNextEnabledMenuItemIndex(flatFocusedMenu, focusedItemIndexInMenu)];
  }
  if (key === 'ArrowRight' && focusedItem.subMenu !== undefined) {
    const firstEnabledIndex = getFirstEnabledMenuItemIndex(flatten(focusedItem.subMenu));
    const rest = firstEnabledIndex === -1 ? [] : [firstEnabledIndex];
    return [...focusIndexStack, ...rest];
  }
  if (key === 'ArrowLeft' && focusIndexStack.length > 1) {
    return focusIndexMenuPath;
  }
  return focusIndexStack;
};

export const handleActionKeyPress = (
  menuItems: InternalMenuItems,
  focusIndexStack: number[],
  onChange: (value?: string) => void,
  onClose: () => void,
  key: ActionKey,
) => {
  if (key === 'Escape') {
    return onClose();
  }
  if (focusIndexStack.length > 0) {
    const focusedItem = getMenuItemFromPath(menuItems, focusIndexStack);
    if (focusedItem !== undefined && !focusedItem.disabled && focusedItem.subMenu === undefined) {
      onChange(focusedItem.id);
      onClose();
    }
  }
};
